package org.example.project.login

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.service.PasswordPolicy
import org.example.project.service.PasswordResetApi
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.PasswordRequirementHint
import org.example.project.utils.DashboardVerlaufHintergrund
import org.example.project.utils.FormStyles
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.css.Style

private fun hashQueryParam(name: String): String {
    val query = window.location.search.substringAfter("?", "")
    if (query.isBlank()) return ""
    return query.split("&")
        .mapNotNull {
            val key = it.substringBefore("=")
            val value = it.substringAfter("=", "")
            if (key == name) js("decodeURIComponent")(value) as String else null
        }
        .firstOrNull()
        .orEmpty()
}

@Composable
fun PasswordResetScreen(onDone: (String) -> Unit) {
    Style(FormStyles)
    DashboardVerlaufHintergrund()

    val scope = rememberCoroutineScope()
    val token = remember { hashQueryParam("token") }
    var mode by remember { mutableStateOf(hashQueryParam("type").ifBlank { "desktop" }) }
    var loading by remember { mutableStateOf(true) }
    var valid by remember { mutableStateOf(false) }
    var newPassword by remember { mutableStateOf("") }
    var repeatPassword by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        LocalizationManager.initialize(hashQueryParam("lang").ifBlank { "de" })
    }

    LaunchedEffect(token) {
        val verifiedMode = PasswordResetApi.verify(token)
        valid = verifiedMode != null
        mode = verifiedMode ?: mode
        loading = false
        if (!valid) message = "Der Link ist ungültig oder abgelaufen."
    }

    fun submit() {
        if (saving || !valid) return
        val qualityError = PasswordPolicy.error(newPassword)
        if (qualityError != null) {
            message = if (mode == "driver") LocalizationManager.get("driver_password_requirement") else qualityError
            return
        }
        if (newPassword != repeatPassword) {
            message = if (mode == "driver") LocalizationManager.get("driver_error_password_not_identical") else "Passwörter stimmen nicht überein."
            return
        }
        saving = true
        message = ""
        scope.launch {
            val completedMode = PasswordResetApi.complete(token, newPassword)
            saving = false
            if (completedMode == null) {
                message = "Passwort konnte nicht zurückgesetzt werden."
                return@launch
            }
            onDone(completedMode)
        }
    }

    Div({
        classes(FormStyles.wrapper)
        style {
            property("position", "relative")
            property("z-index", "1")
        }
    }) {
        Div({
            classes(FormStyles.box)
            style { gap(16.px) }
        }) {
            H1({ classes(FormStyles.h1) }) {
                Text(if (mode == "driver") LocalizationManager.get("driver_change_password") else "Passwort zurücksetzen")
            }
            P({ classes(FormStyles.h3) }) {
                Text(
                    when {
                        loading -> "Link wird geprüft..."
                        valid -> if (mode == "driver") LocalizationManager.get("driver_first_login_password_hint") else "Bitte lege ein neues Passwort fest."
                        else -> message
                    }
                )
            }
            if (valid && !loading) {
                Input(InputType.Password, attrs = {
                    classes(FormStyles.input)
                    placeholder(if (mode == "driver") LocalizationManager.get("driver_new_password") else "Neues Passwort")
                    value(newPassword)
                    onInput { newPassword = it.value; message = "" }
                    onKeyDown {
                        if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                            it.preventDefault()
                            submit()
                        }
                    }
                })
                PasswordRequirementHint(
                    newPassword,
                    if (mode == "driver") LocalizationManager.get("driver_password_requirement") else PasswordPolicy.REQUIREMENT_TEXT
                )
                Input(InputType.Password, attrs = {
                    classes(FormStyles.input)
                    placeholder(if (mode == "driver") LocalizationManager.get("driver_repeat_password") else "Passwort wiederholen")
                    value(repeatPassword)
                    onInput { repeatPassword = it.value; message = "" }
                    onKeyDown {
                        if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                            it.preventDefault()
                            submit()
                        }
                    }
                })
                if (message.isNotBlank()) P({ classes(FormStyles.h4) }) { Text(message) }
                Div({ classes(FormStyles.buttonRow) }) {
                    LumaticaButton(
                        label = if (saving) "..." else if (mode == "driver") LocalizationManager.get("driver_change_password_action") else "Passwort speichern",
                        onClick = { submit() },
                        variant = LumaticaButtonVariant.Action,
                        enabled = !saving,
                        heightPx = 44,
                        fillWidth = true
                    )
                }
            }
        }
    }
}
